import { useEffect, useRef, useState } from 'react';
import {
  logVisit, updateVisit, deleteVisit, uploadVisitPhotos, deleteVisitPhoto,
  fileUrl, phoneZone, MAX_VISIT_PHOTOS,
} from '../lib/api.js';
import { dayLabel, dayOfWeek, zoneDate, zoneTime, mapsHref, isMappable, isoInZone }
  from '../lib/format.js';
import Icon from '../components/Icon.jsx';
import SwipeToDelete from '../components/Swipe.jsx';

// The diary · what actually happened.
//
// Everything else in the app is the plan: what is booked, what is being decided,
// what it costs. None of it survives contact with a real day — half of what you
// end up doing was never in it, and some of what was in it did not happen. This
// is the only record of the difference, and the only thing worth reading a year
// later.
//
// It reads forward, oldest first, because that is the shape of a story and
// because the tab exists to be read as one. Adding is at the top where the
// thumb is.

// Every entry is read on the clock it was written on, and everything the diary
// decides for itself — today, the default time on the form — is read on the
// phone's. One zone throughout, or the same afternoon appears twice.
const dayOf = (v, zone) => zoneDate(Date.parse(v.visited_at), v.timezone ?? zone);

export default function Diary({ data, onChange }) {
  const [adding, setAdding] = useState(false);
  // Which entries are open. Held here rather than in each entry because "apri
  // tutto" is one of the two things this list is for — the other being to be
  // skimmable, which it is not if every note and photograph is on screen.
  const [open, setOpen] = useState(() => new Set());
  const zone = phoneZone();
  const today = zoneDate(Date.now(), zone);
  const visits = [...data.visits].sort((a, b) =>
    String(a.visited_at).localeCompare(String(b.visited_at)));

  const days = [];
  for (const v of visits) {
    const d = dayOf(v, zone);
    if (!days.length || days[days.length - 1].date !== d) days.push({ date: d, entries: [] });
    days[days.length - 1].entries.push(v);
  }

  const shots = visits.reduce((n, v) => n + (v.photos?.length ?? 0), 0);
  const allOpen = visits.length > 0 && visits.every((v) => open.has(v.id));
  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(visits.map((v) => v.id)));
  const toggle = (id) => setOpen((cur) => {
    const next = new Set(cur);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <>
      <div className="today-head">
        <p className="kicker">Il diario</p>
        <h2>Quello che abbiamo fatto</h2>
        <p className="where">
          {visits.length === 0
            ? 'Ancora niente segnato'
            : [`${visits.length} ${visits.length === 1 ? 'cosa' : 'cose'}`,
               `${days.length} ${days.length === 1 ? 'giorno' : 'giorni'}`,
               shots ? `${shots} ${shots === 1 ? 'foto' : 'foto'}` : null,
              ].filter(Boolean).join(' · ')}
        </p>
      </div>

      {adding
        ? <AddVisit trip={data.trip} zone={zone} today={today}
                    onDone={() => setAdding(false)} onChange={onChange} />
        : (
          <button className="btn primary" style={{ marginTop: 'var(--s-3)' }}
                  onClick={() => setAdding(true)}>
            <Icon name="plus" size={15} /> Segna qualcosa
          </button>
        )}

      {visits.length > 0 && (
        <div className="diarytools">
          <button className="act" onClick={toggleAll} aria-expanded={allOpen}>
            <Icon name="chevronRight" size={13}
                  style={{ rotate: allOpen ? '90deg' : '0deg' }} />
            {allOpen ? 'Chiudi tutto' : 'Apri tutto'}
          </button>
        </div>
      )}

      {visits.length === 0 && !adding && (
        <p className="day-empty" style={{ marginTop: 'var(--s-5)' }}>
          Segna quello che fate, anche solo il nome e una foto. Serve a ricordarlo
          e serve a Claude per scrivere il resoconto.
        </p>
      )}

      {days.map((day) => (
        <section key={day.date} className="section">
          <div className="day-h">
            <b>{dayLabel(day.date)}</b>
            <span className="dow">{dayOfWeek(day.date)}</span>
            {day.date === today && <span className="where">oggi</span>}
          </div>
          <div className="stack">
            {day.entries.map((v) => (
              <SwipeToDelete key={v.id} label="Togli"
                             prompt={`Togliere «${v.title}» dal diario?`}
                             onDelete={async () => { await deleteVisit(v); await onChange?.(); }}>
                <Entry visit={v} onChange={onChange}
                       open={open.has(v.id)} onToggle={() => toggle(v.id)} />
              </SwipeToDelete>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/* ── one entry ───────────────────────────────────────────────────────────── */

// Closed, an entry is a time and a name — which is what makes a fortnight of
// them readable in one screen. Everything else is one tap away, and the closed
// line says what is under it rather than hiding it silently.
function Entry({ visit: v, onChange, open, onToggle }) {
  const camera = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(v.note ?? '');
  const zone = v.timezone ?? phoneZone();
  const at = zoneTime(Date.parse(v.visited_at), zone);
  // The time is editable because it is now the only one shown anywhere for this
  // entry: an entry logged from a card takes the time it was planned for, which
  // is right until the morning you went at eleven instead of nine.
  const [time, setTime] = useState(at);
  const shots = v.photos?.length ?? 0;
  const hint = [v.note ? 'nota' : null, shots ? `${shots} foto` : null]
    .filter(Boolean).join(' · ');

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onChange?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <article className="diaryentry">
      <button className="head" aria-expanded={open} onClick={onToggle}>
        <span className="t">{at}</span>
        <b>{v.title}</b>
        {open && v.event_id && <span className="state ok">in programma</span>}
        {!open && hint && <span className="hint">{hint}</span>}
        <Icon name="chevronRight" size={14} className="caret"
              style={{ rotate: open ? '90deg' : '0deg' }} />
      </button>

      {open && v.note && !editing && <p className="note">{v.note}</p>}

      {open && editing && (
        <div className="notebox">
          <textarea value={note} rows={3} autoFocus
                    placeholder="Com'è andata: la coda, il tempo, cosa diresti a chi ci va dopo."
                    onChange={(e) => setNote(e.target.value)} />
          <div className="when">
            <label>A che ora
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
          </div>
          <div className="actions">
            <button className="act quiet"
                    onClick={() => { setNote(v.note ?? ''); setTime(at); setEditing(false); }}>
              Annulla
            </button>
            <button className="act strong" disabled={busy}
                    onClick={() => run(async () => {
                      const day = zoneDate(Date.parse(v.visited_at), zone);
                      await updateVisit(v.id, {
                        note: note.trim() || null,
                        // Same day, corrected clock. Moving an entry to another
                        // day is a different act and does not belong on a form
                        // whose subject is "when were we there".
                        ...(time && time !== at
                          ? { visited_at: isoInZone(day, time, zone) } : {}),
                      });
                      setEditing(false);
                    })}>
              Salva
            </button>
          </div>
        </div>
      )}

      {open && shots > 0 && (
        <div className="shots">
          {v.photos.map((p) => (
            <Thumb key={p.id} photo={p} busy={busy}
                   onDelete={() => run(async () => {
                     if (confirm('Eliminare la foto?')) await deleteVisitPhoto(p);
                   })} />
          ))}
        </div>
      )}

      {open && (
      <div className="actions">
        {isMappable(v.place) && (
          <a className="act" href={mapsHref(v.place)} target="_blank" rel="noreferrer">
            <Icon name="pin" size={13} /> Mappa
          </a>
        )}
        {!editing && (
          <button className="act" onClick={() => setEditing(true)}>
            <Icon name="note" size={13} /> {v.note ? 'Modifica' : 'Nota e ora'}
          </button>
        )}
        {shots < MAX_VISIT_PHOTOS && (
          <button className="act" disabled={busy} onClick={() => camera.current?.click()}>
            <Icon name="camera" size={13} /> Foto
          </button>
        )}
        <button className="act quiet" disabled={busy}
                onClick={() => run(async () => {
                  if (confirm(`Togliere «${v.title}» dal diario?`)) await deleteVisit(v);
                })}>
          Togli
        </button>
      </div>
      )}

      <input ref={camera} className="hidden-input" type="file" accept="image/*"
             onChange={(e) => {
               const files = e.target.files;
               if (!files?.length) return;
               run(() => uploadVisitPhotos(v, files))
                 .finally(() => { if (camera.current) camera.current.value = ''; });
             }} />
    </article>
  );
}

// A photo lives in a private bucket, so it is fetched through a signed URL that
// expires. Minted per thumbnail, on demand — never a public link.
function Thumb({ photo, busy, onDelete }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    fileUrl(photo, 600).then((u) => { if (alive) setSrc(u); }).catch(() => {});
    return () => { alive = false; };
  }, [photo.id]);

  return (
    <div className="shot">
      <button className="open" disabled={!src}
              onClick={() => src && window.open(src, '_blank', 'noopener')}>
        {src
          ? <img src={src} alt={photo.caption ?? photo.file_name ?? 'foto'} loading="lazy" />
          : <span className="ph" />}
      </button>
      <button className="del" disabled={busy} onClick={onDelete} aria-label="Elimina la foto">×</button>
    </div>
  );
}

/* ── logging something that was never in the plan ────────────────────────── */

// The warung nobody named, the beach you stopped at. This is the half of the
// trip the itinerary cannot account for, so it takes a name and nothing else:
// the day and the time are already filled in, and the photo can come first.
export function AddVisit({ trip, zone, today, onDone, onChange }) {
  const camera = useRef(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today);
  const [time, setTime] = useState(() => zoneTime(Date.now(), zone));
  const [note, setNote] = useState('');
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const visit = await logVisit({
        trip_id: trip.id,
        title: title.trim(),
        visited_at: isoInZone(date, time, zone) ?? new Date().toISOString(),
        timezone: zone,
        note: note.trim() || null,
      });
      if (shot) await uploadVisitPhotos({ ...visit, photos: [] }, [shot]);
      await onChange?.();
      onDone();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="addvisit">
      <input className="title" value={title} autoFocus placeholder="Che cosa avete fatto?"
             onChange={(e) => setTitle(e.target.value)} />
      <div className="when">
        <label>Giorno <input type="date" value={date}
                             onChange={(e) => setDate(e.target.value)} /></label>
        <label>Ora <input type="time" value={time}
                          onChange={(e) => setTime(e.target.value)} /></label>
      </div>
      <textarea rows={2} value={note} placeholder="Una nota, se vale la pena"
                onChange={(e) => setNote(e.target.value)} />
      <div className="actions">
        <button className="act" onClick={() => camera.current?.click()}>
          <Icon name="camera" size={13} /> {shot ? shot.name.slice(0, 18) : 'Foto'}
        </button>
        <button className="act quiet" onClick={onDone}>Annulla</button>
        <button className="btn primary" disabled={busy || !title.trim()} onClick={save}>
          {busy ? 'Salvo…' : 'Segna'}
        </button>
      </div>
      <input ref={camera} className="hidden-input" type="file" accept="image/*"
             capture="environment"
             onChange={(e) => setShot(e.target.files?.[0] ?? null)} />
    </div>
  );
}
